
import { defineComponent, ref, computed, watch, PropType } from 'vue'

// 汉字首字母映射（覆盖常见股票板块名称用字）
const CHAR_PINYIN: Record<string, string> = {
  '爱':'A','安':'A','奥':'A','白':'B','包':'B','宝':'B','北':'B','贝':'B','本':'B','比':'B','标':'B','冰':'B','博':'B','布':'B',
  '财':'C','采':'C','草':'C','茶':'C','长':'C','超':'C','车':'C','城':'C','成':'C','出':'C','传':'C','创':'C','磁':'C',
  '大':'D','代':'D','单':'D','地':'D','电':'D','动':'D','多':'D',
  '二':'E',
  '房':'F','纺':'F','飞':'F','风':'F','服':'F','福':'F','复':'F',
  '钢':'G','高':'G','工':'G','公':'G','股':'G','光':'G','广':'G','国':'G','贵':'G',
  '海':'H','航':'H','核':'H','黑':'H','恒':'H','化':'H','环':'H','互':'H','华':'H','汇':'H','火':'H',
  '机':'J','基':'J','计':'J','家':'J','建':'J','交':'J','教':'J','金':'J','军':'J','酒':'J','居':'J',
  '科':'K','矿':'K',
  '蓝':'L','乐':'L','冷':'L','粮':'L','林':'L','零':'L','旅':'L',
  '煤':'M','贸':'M','美':'M','民':'M','模':'M','摩':'M',
  '能':'N','农':'N',
  '普':'P',
  '汽':'Q','气':'Q','钱':'Q','轻':'Q','清':'Q','区':'Q',
  '软':'R','燃':'R',
  '三':'S','商':'S','生':'S','食':'S','石':'S','数':'S','水':'S','塑':'S',
  '太':'T','碳':'T','通':'T','铁':'T','题':'T',
  '文':'W','物':'W',
  '稀':'X','新':'X','信':'X','星':'X','休':'X','畜':'X',
  '医':'Y','仪':'Y','银':'Y','印':'Y','饮':'Y','油':'Y','有':'Y','娱':'Y','运':'Y',
  '造':'Z','证':'Z','智':'Z','中':'Z','重':'Z','装':'Z','资':'Z','综':'Z'
}

function getFirstLetter(name: string): string {
  if (!name) return '#'
  const first = name[0]
  // 已经是字母
  if (/^[A-Za-z]$/.test(first)) return first.toUpperCase()
  // 查映射表
  return CHAR_PINYIN[first] || '#'
}

export default defineComponent({
  name: 'BoardSelectorPopup',
  props: {
    visible: Boolean,
    title: { type: String, default: '' },
    options: { type: Array as PropType<any[]>, default: () => [] },
    loading: Boolean,
    initialSelected: { type: Array as PropType<any[]>, default: () => [] },
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true }
  },
  emits: ['confirm', 'cancel'],
  setup(props, { emit }) {
    const pending = ref<any[]>([])

    // 每次弹框打开时同步初始已选
    watch(() => props.visible, (v) => {
      if (v) pending.value = [...props.initialSelected]
    }, { immediate: true })

    // options 加载完后，用 attrCode 重新对齐 pending（确保引用正确）
    watch(() => props.options, (opts) => {
      if (!props.visible || !opts.length) return
      // 用 options 里的完整对象替换 pending 里的同 attrCode 项，保留选中状态
      const codeMap = new Map(opts.map((o: any) => [o.attrCode, o]))
      pending.value = pending.value.map((p: any) => codeMap.get(p.attrCode) || p)
      console.log('[BSP] pending after align:', JSON.stringify(pending.value))
    })

    const pendingCodes = computed(() => new Set(pending.value.map((o: any) => o.attrCode)))

    // 按首字母分组，严格按 A-Z 排列，非字母归 '#'
    const groupedOptions = computed(() => {
      const map = new Map<string, any[]>()
      for (const opt of props.options) {
        // 优先用接口返回的 firstLetter，否则从名字推断
        const raw = opt.firstLetter || getFirstLetter(opt.attrName)
        const letter = /^[A-Z]$/.test(raw.toUpperCase()) ? raw.toUpperCase() : '#'
        if (!map.has(letter)) map.set(letter, [])
        map.get(letter)!.push(opt)
      }
      // 按 A-Z 排序，# 放最后
      const letters = Array.from(map.keys()).sort((a, b) => {
        if (a === '#') return 1
        if (b === '#') return -1
        return a.localeCompare(b)
      })
      return letters.map(letter => ({ letter, items: map.get(letter)! }))
    })

    const isAllSelected = computed(() =>
      props.options.length > 0 && pending.value.length === props.options.length
    )
    const isPartial = computed(() =>
      pending.value.length > 0 && pending.value.length < props.options.length
    )

    const toggleAll = () => {
      if (isAllSelected.value) pending.value = []
      else pending.value = [...props.options]
    }

    const toggleOption = (opt: any) => {
      const idx = pending.value.findIndex((o: any) => o.attrCode === opt.attrCode)
      if (idx >= 0) pending.value.splice(idx, 1)
      else pending.value.push(opt)
    }

    const canConfirm = computed(() => pending.value.length > 0)
    const confirmDisabled = computed(() => props.loading || !canConfirm.value)
    const confirmBtnStyle = computed(() => ({
      background: confirmDisabled.value ? '#ccc' : '#e8392a',
      color: '#fff',
      cursor: confirmDisabled.value ? 'not-allowed' : 'pointer',
    }))

    const onConfirm = () => {
      if (!canConfirm.value) return
      emit('confirm', [...pending.value])
    }

    const checkboxStyle = (active: boolean) => ({
      borderColor: active ? '#e8392a' : '#ccc',
      background: active ? '#e8392a' : 'transparent'
    })

    return {
      pending, pendingCodes, groupedOptions,
      isAllSelected, isPartial,
      toggleAll, toggleOption, onConfirm, checkboxStyle,
      canConfirm, confirmDisabled, confirmBtnStyle,
    }
  }
})
